import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent, { hasConsent } from './components/CookieConsent';
import { fetchProducts } from './api/api';
import { CartProvider } from './context/CartContext';
import { UserProvider } from './context/UserContext';
import { fetchBranding, getImageUrl } from './api/api';
import api from './api/api';
import { CurrencyProvider } from './context/CurrencyContext';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

// Lazy-load page components for code splitting
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Admin = lazy(() => import('./pages/Admin'));
const Cart = lazy(() => import('./pages/Cart'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const BikeToWork = lazy(() => import('./pages/BikeToWork'));
const Orders = lazy(() => import('./pages/Orders'));
const Login = lazy(() => import('./pages/Login'));
const Support = lazy(() => import('./pages/Support'));
const Services = lazy(() => import('./pages/Services'));
const RentalBooking = lazy(() => import('./pages/RentalBooking'));

// PolicyPage is a named export from Support
const PolicyPageLazy = lazy(() => import('./pages/Support').then(m => ({ default: m.PolicyPage })));

export const GatewayContext = React.createContext({ paypal: { enabled: true }, stripe: { enabled: false } });

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ProductDetailRedirect() {
  const { model } = useParams();
  const navigate = useNavigate();
  React.useEffect(() => {
    fetchProducts().then(data => {
      const modelLower = (model || '').toLowerCase();
      const modelSlug = modelLower.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const found = data.find(p => {
        const pModel = (p.model || '').toLowerCase();
        const pName = (p.name || '').toLowerCase();
        const pNameSlug = pName.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        return pModel === modelLower || pModel === modelSlug || (p._id || p.id) === model || pName === modelLower || pNameSlug === modelSlug;
      });
      if (found) {
        const slug = found.model || found.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        navigate(`/products/${found.category}/${slug}`, { replace: true });
      } else {
        navigate('/products', { replace: true });
      }
    }).catch(() => navigate('/products', { replace: true }));
  }, [model, navigate]);
  return <div style={{ padding: 80, textAlign: 'center', color: '#999' }}>Loading...</div>;
}

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname === '/admin';

  const [consentGiven, setConsentGiven] = useState(hasConsent());

  const injectTracking = (b) => {
    if (location.pathname === '/admin') return;
    // Google Analytics 4 (gtag.js)
    if (b.googleAnalyticsId && !document.getElementById('ga-script')) {
        const gs = document.createElement('script');
        gs.id = 'ga-script';
        gs.async = true;
        gs.src = 'https://www.googletagmanager.com/gtag/js?id=' + b.googleAnalyticsId;
        document.head.appendChild(gs);
        let gtagConfigs = 'window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag("js",new Date());gtag("config","' + b.googleAnalyticsId + '");';
        if (b.googleAdsId) gtagConfigs += 'gtag("config","' + b.googleAdsId + '");';
        const gi = document.createElement('script');
        gi.id = 'ga-inline';
        gi.textContent = gtagConfigs;
        document.head.appendChild(gi);
      }
    // Google Ads (standalone if no GA4)
    if (b.googleAdsId && !b.googleAnalyticsId && !document.getElementById('gads-script')) {
      const as = document.createElement('script');
      as.id = 'gads-script';
      as.async = true;
      as.src = 'https://www.googletagmanager.com/gtag/js?id=' + b.googleAdsId;
      document.head.appendChild(as);
      const ai = document.createElement('script');
      ai.id = 'gads-inline';
      ai.textContent = 'window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag("js",new Date());gtag("config","' + b.googleAdsId + '");';
      document.head.appendChild(ai);
    }
    // Google Tag Manager
    if (b.gtmId && !document.getElementById('gtm-script')) {
      const ts = document.createElement('script');
      ts.id = 'gtm-script';
      ts.textContent = '(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({"gtm.start":new Date().getTime(),event:"gtm.js"});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!="dataLayer"?"&l="+l:"";j.async=true;j.src="https://www.googletagmanager.com/gtm.js?id="+i+dl;f.parentNode.insertBefore(j,f);})(window,document,"script","dataLayer","' + b.gtmId + '");';
      document.head.appendChild(ts);
      const ns = document.createElement('noscript');
      ns.id = 'gtm-noscript';
      const iframe = document.createElement('iframe');
      iframe.src = 'https://www.googletagmanager.com/ns.html?id=' + b.gtmId;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      ns.appendChild(iframe);
      document.body.insertBefore(ns, document.body.firstChild);
    }
    // Meta Pixel (Facebook)
    if (b.metaPixelId && !document.getElementById('meta-pixel-script')) {
      const ms = document.createElement('script');
      ms.id = 'meta-pixel-script';
      ms.textContent = '!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version="2.0";n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,"script","https://connect.facebook.net/en_US/fbevents.js");fbq("init","' + b.metaPixelId + '");fbq("track","PageView");';
      document.head.appendChild(ms);
      const mn = document.createElement('noscript');
      mn.id = 'meta-pixel-noscript';
      mn.innerHTML = '<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=' + b.metaPixelId + '&ev=PageView&noscript=1" />';
      document.head.appendChild(mn);
    }
    // Custom head scripts (injected raw)
    if (b.customHeadScripts && !document.getElementById('custom-head-scripts')) {
      const cs = document.createElement('div');
      cs.id = 'custom-head-scripts';
      cs.innerHTML = b.customHeadScripts;
      cs.querySelectorAll('script').forEach(s => {
        const ns = document.createElement('script');
        if (s.src) ns.src = s.src;
        if (s.textContent) ns.textContent = s.textContent;
        ns.async = true;
        document.head.appendChild(ns);
      });
    }
    // Custom meta tags (e.g. google-site-verification)
    if (b.customMetaTags && !document.getElementById('custom-meta-tags')) {
      const wrapper = document.createElement('div');
      wrapper.id = 'custom-meta-tags';
      wrapper.style.display = 'none';
      document.head.appendChild(wrapper);
      const temp = document.createElement('div');
      temp.innerHTML = b.customMetaTags;
      temp.querySelectorAll('meta').forEach(m => document.head.appendChild(m));
    }
  };

  const brandingRef = React.useRef(null);

  useEffect(() => {
    fetchBranding().then(b => {
      if (b.siteName) document.title = b.siteName;
      if (b.favicon) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        link.href = getImageUrl(b.favicon);
      }
      brandingRef.current = b;
      if (hasConsent()) injectTracking(b);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (consentGiven && brandingRef.current) injectTracking(brandingRef.current);
  }, [consentGiven]);

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Header />}
      <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="page-loader">Loading...</div></div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/products/:category/:model" element={<ProductDetail />} />
          <Route path="/products/:model" element={<ProductDetailRedirect />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/login" element={<Login />} />
          <Route path="/services" element={<Services />} />
          <Route path="/rental-booking" element={<RentalBooking />} />
          <Route path="/support" element={<Support />} />
          <Route path="/support/:policy" element={<PolicyPageLazy />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/bike-to-work" element={<BikeToWork />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </Suspense>
      {!isAdmin && <Footer />}
      {!isAdmin && <CookieConsent onAccept={() => setConsentGiven(true)} />}
    </>
  );
}

function App() {
  const [gateways, setGateways] = useState(null);

  useEffect(() => {
    api.get('/payment-gateways').then(({ data }) => setGateways(data)).catch(() =>
      setGateways({ paypal: { enabled: true, clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID, mode: 'sandbox' }, stripe: { enabled: false } })
    );
  }, []);

  if (!gateways) return null;

  const paypalClientId = gateways.paypal?.clientId || process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb';

  return (
    <HelmetProvider>
    <GatewayContext.Provider value={gateways}>
      <CurrencyProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'EUR', intent: 'capture' }}>
          <CartProvider>
            <UserProvider>
              <AppContent />
            </UserProvider>
          </CartProvider>
        </PayPalScriptProvider>
      </Router>
      </CurrencyProvider>
    </GatewayContext.Provider>
    </HelmetProvider>
  );
}

export default App;
